/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import {
    Button,
    Card,
    CardHeader,
    CardBody,
    FormGroup,
    Form,
    Input,
    InputGroupAddon,
    InputGroupText,
    InputGroup,
    Row,
    Col,
} from "reactstrap";
import React, { useRef, useState, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { toast, Zoom } from "react-toastify";
import axios, { AxiosResponse } from "axios";

import { PATHS } from "../../routes";
import { API } from "../../constants";
import { resetPassword } from "../../Services/UserAuth/UserAuthenticate";
import { TypeResetPassword } from "../../types";

const ForgotPassword = () => {

    const history = useHistory();
    const [resetToken, setResetToken] = useState<string>('');

    const password1 = useRef<any>();
    const password2 = useRef<any>();
    const submit = useRef<any>();
    const firstRender = useRef<boolean>(true); // we use the help of useRef to test if it's the first render

    const [passwordNew, setPasswordNew] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');

    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [isLoading, setIsLoading] = useState<boolean>(false); // for submiting form
    const [disable, setDisabled] = useState<boolean>(true); // set a state variable which can be used to disable the save/submit button
    //  set error messages to display to the user
    const [nameError, setNameError] = useState<string>('');

    useEffect(() => {
        // reads token from url
        const url_string = window.location.href;
        const url = new URL(url_string);
        let token: any = url.searchParams.get("token");
        setResetToken(token);

        // skip validation on first render
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        // console.log("Token: ", resetToken);

        setDisabled(formValidation() || false);
    }, [passwordNew, passwordConfirm]);

    const handleResetPassword = (e) => {
        e.preventDefault();
        setIsLoading(true);

        let resetPasswordInfo: TypeResetPassword = {
            token: resetToken,
            password: passwordConfirm.trim()
        };
        // console.log(resetPasswordInfo);

        resetPassword(resetPasswordInfo).then(
            (response: AxiosResponse) => {
                // console.log("Response Received: ", response);
                // history.push(PATHS.login);
                setPasswordNew("");
                setPasswordConfirm("");
                toast.success("Password reset successfull! Try to log in", {
                    position: "bottom-center",
                    transition: Zoom,
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
                setTimeout(() => window.location.reload, 3000);
            }, (error) => {
                console.log("Error Caught: ", error?.response);
                toast.error("Can not reset password! Try again");
            }
        ).catch((error) => console.log("Catched error ---> ", error)).finally(() => setIsLoading(false));
    };

    const formValidation = () => {
        if (passwordNew === "") {
            setNameError("Password can not be empty!");
            return true;
        } else if (passwordNew !== passwordConfirm) {
            setNameError("Confirm password must be same as password!");
            return true;
        } else if (passwordNew.length < 5 || passwordConfirm.length < 5) {
            setNameError("Password length must be of minimum 5 characters!")
        } else if (passwordNew.length !== passwordConfirm.length) {
            setNameError("Password length must be same!");
        } else {
            setNameError("");
            return false;
        }
    };

    return (
        <React.Fragment>
            <Col lg="5" md="7">
                <Card className="bg-secondary shadow border-0">
                    <CardBody className="px-lg-5 py-lg-5">
                        <div className="text-center text-muted mb-4">
                            <small>Enter new password</small>
                        </div>
                        <Form role="form">
                            <FormGroup style={{ position: 'relative' }}>
                                <InputGroup className="input-group-alternative">
                                    <InputGroupAddon addonType="prepend" className="pointer">
                                        <InputGroupText>
                                            {/* <i className="ni ni-lock-circle-open" /> */}
                                            {!showPassword ? <i className="fas fa-eye"
                                                onClick={() => setShowPassword(!showPassword)}></i>
                                                :
                                                <i className="fas fa-eye-slash"
                                                    onClick={() => setShowPassword(!showPassword)}></i>}
                                        </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Password"
                                        autoComplete="new-password"
                                        id="firstPassword"
                                        autoFocus
                                        ref={password1}
                                        onChange={(e) => {
                                            setPasswordNew(e.target.value);
                                        }}
                                    // onKeyDown={onKeyDownHandler}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <FormGroup style={{ position: 'relative' }}>
                                <InputGroup className="input-group-alternative">
                                    <InputGroupAddon addonType="prepend" className="pointer">
                                        <InputGroupText>
                                            {/* <i className="ni ni-lock-circle-open" /> */}
                                            {!showPassword ? <i className="fas fa-eye"
                                                onClick={() => setShowPassword(!showPassword)}></i>
                                                :
                                                <i className="fas fa-eye-slash"
                                                    onClick={() => setShowPassword(!showPassword)}></i>}
                                        </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Confirm Password"
                                        autoComplete="new-password"
                                        id="secondPassword"
                                        ref={password2}
                                        onChange={(e) => {
                                            setPasswordConfirm(e.target.value);
                                        }}
                                    // onKeyDown={onKeyDownHandler}
                                    />
                                </InputGroup>
                            </FormGroup>
                            <div>
                                {nameError && (
                                    <p className="text-danger mt-0 rounded small">*{nameError}</p>
                                )}
                            </div>
                            <div className="text-center">
                                {isLoading ? (<Button className="my-4" color="primary" size="block" type="button" >
                                    <div
                                        className="spinner-border rounded-circle spinner-border-sm"
                                        role="status"
                                    >
                                        <p className="sr-only">Loading...</p>
                                    </div>
                                </Button>
                                ) : (
                                    <Button className="my-4" color="primary" size="block"
                                        type="button"
                                        id="submitForm"
                                        ref={submit}
                                        disabled={disable}
                                        onClick={(e) => handleResetPassword(e)}>
                                        Reset Password <i className="fas fa-key pl-2"></i>
                                    </Button>)}
                            </div>
                        </Form>
                    </CardBody>
                </Card>
                {/* <Row className="mt-3">
                    <Col xs="6">
                        <a
                  className="text-light"
                  href="#pablo"
                  onClick={(e) => { e.preventDefault(); history.push(PATHS.login) }}
                >
                  <small>Go to login?</small>
                </a>
                    </Col>
                    <Col className="text-right text-light pointer" xs="6">
                        <a
                            onClick={(e) => { e.preventDefault(); history.push(PATHS.login) }}
                        >
                            <small>Go to login?</small>
                        </a>
                    </Col>
                </Row> */}
            </Col>
        </React.Fragment>
    );
};

export default ForgotPassword;
